import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./upload-attachment.module.scss";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import attachIcon from "@src/assets/svg/attach-icon.svg";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { postUploadAttachmentUseCase } from "@src/cloud-connect/support/use-cases/post-upload-attachment-use-case";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";

export interface UploadedAttachment {
  file: File;
  token: string;
}

export interface UploadAttachmentHandle {
  uploadFile: (file: File) => Promise<void>;
}

interface UploadAttachmentProps {
  onAttachmentChange: (attachment: UploadedAttachment | null) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  hasAttachment?: boolean;
  accept?: string;
  maxSizeBytes?: number;
  attachmentRemoveKey?: number;
}

const cssClassName = "upload-attachment";

const DEFAULT_MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

export const UploadAttachment = forwardRef<
  UploadAttachmentHandle,
  UploadAttachmentProps
>(
  (
    {
      onAttachmentChange,
      onUploadingChange,
      disabled = false,
      disabledTooltip = "",
      hasAttachment = false,
      accept = "*/*",
      maxSizeBytes = DEFAULT_MAX_ATTACHMENT_SIZE_BYTES,
      attachmentRemoveKey = 0,
    },
    ref,
  ) => {
    const attachmentInputRef = useRef<HTMLInputElement>(null);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

    useEffect(() => {
      onUploadingChange?.(isUploadingAttachment);
    }, [isUploadingAttachment, onUploadingChange]);

    useEffect(() => {
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    }, [attachmentRemoveKey]);

    const handleAttachmentClick = () => {
      if (disabled) return;
      attachmentInputRef.current?.click();
    };

    const uploadFile = useCallback(
      async (selectedFile: File) => {
        if (disabled || hasAttachment || isUploadingAttachment) {
          return;
        }

        // Extension/image validation happens on the backend — its 422 detail
        // message is surfaced verbatim via the upload error snackbar.
        if (selectedFile.size > maxSizeBytes) {
          errorSnackbar("Attachment size must be 10MB or less.");
          return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile, selectedFile.name);

        setIsUploadingAttachment(true);

        try {
          const result = await postUploadAttachmentUseCase(formData);

          if (!result.success) {
            errorSnackbar(result.error);
            return;
          }

          const token = result?.data?.token;
          if (!token) {
            errorSnackbar("Attachment upload token is missing.");
            return;
          }

          onAttachmentChange({
            file: selectedFile,
            token,
          });
        } finally {
          setIsUploadingAttachment(false);
        }
      },
      [
        disabled,
        hasAttachment,
        isUploadingAttachment,
        maxSizeBytes,
        onAttachmentChange,
      ],
    );

    useImperativeHandle(ref, () => ({ uploadFile }), [uploadFile]);

    const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      e.target.value = "";
      if (selectedFile) {
        void uploadFile(selectedFile);
      }
    };

    return (
      <div className={styles[cssClassName]}>
        <input
          ref={attachmentInputRef}
          type="file"
          accept={accept}
          onChange={handleAttachmentSelect}
          disabled={disabled}
          style={{ display: "none" }}
        />

        {isUploadingAttachment ? (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <SpinnerWithVariant variant="small" />
          </div>
        ) : (
          <IconButton
            fun={handleAttachmentClick}
            icon={attachIcon}
            disabled={disabled || isUploadingAttachment || hasAttachment}
            tooltip={disabled ? disabledTooltip : ""}
          />
        )}
      </div>
    );
  },
);

UploadAttachment.displayName = "UploadAttachment";
